// Pure view-model for the review list (stories 58-63): what each row says about itself (how many
// inputs are still Assumed, which migration re-check flags are open, which record levels are on) and
// the metric history behind each use case. No React, no store, so tests build the inputs directly.

import { DEFAULT_ACV, DEFAULT_CURRENT_ANNUAL_PIPELINE, DEFAULT_WIN_RATE, getSizedUseCase, type SizedUseCase } from '../../data/use-case-catalog';
import { TOOLS16, DEFAULT_TOOLS16 } from '../../data/tools';
import { fmtInput, moneyBig } from '../../engine/format';
import type { MetricHistoryEntry, MetricHistoryMap } from '../../engine/metric-history';
import type { Recheck } from '../../engine/migrate-v3';
import {
  computeCalculator,
  countInputsOnDefaults,
  enabledLevels,
  enabledMethods,
  orderSelection,
  overlapNotesFor,
  requiresCurrentAnnualPipeline,
  type CalculatorInput,
  type UseCaseSelection,
} from '../../engine/use-case-engine';
import { attributedLabelFor } from './attributed-label';
import { LEVEL_LABEL, formatMetric, methodLetter, questionGroups } from './use-case-page-model';

/* ------------------------------------------------------------------------ re-check flags */

/** Plain-language label for a flag kind, shown beside the "Re-check" chip. */
export const RECHECK_KIND_LABEL: Record<Recheck['kind'], string> = {
  dropped: 'Not carried over',
  new: 'New question',
  conflict: 'Conflicts with a v3 default',
  factors: 'Factors reset',
  scenario: 'Scenario retired',
  retired: 'Use case retired',
  unsupported: 'Not supported in v3',
  estimate: 'Recovered estimate',
};

export interface RecheckVm {
  flag: Recheck;
  kindLabel: string;
  reason: string;
  /** The v2 value that was dropped, when there was one, so the SE can re-enter it. */
  was: string | null;
}

function recheckVm(flag: Recheck): RecheckVm {
  const was = typeof flag.dropped === 'number' ? flag.dropped.toLocaleString('en-US') : null;
  return { flag, kindLabel: RECHECK_KIND_LABEL[flag.kind], reason: flag.reason, was };
}

/** Flags for one use case, in the order the migration raised them. */
export function recheckFor(flags: readonly Recheck[], useCaseId: string): RecheckVm[] {
  return flags.filter((f) => f.useCaseId === useCaseId).map(recheckVm);
}

/** Deal-level and global flags (no use case), shown above the list. */
export function dealRecheck(flags: readonly Recheck[]): RecheckVm[] {
  return flags.filter((f) => f.useCaseId === null || !getSizedUseCase(f.useCaseId)).map(recheckVm);
}

/** question key -> reason, so an input row can carry its own "Re-check" tag. */
export function flaggedKeys(flags: readonly Recheck[], useCaseId: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of flags) if (f.useCaseId === useCaseId && !(f.key in out)) out[f.key] = f.reason;
  return out;
}

/* ----------------------------------------------------------------------------- rows */

export interface ReviewRowVm {
  id: string;
  name: string;
  tier: SizedUseCase['tier'];
  /** Inputs still on the Clay default / inputs asked at the enabled record levels. */
  assumed: number;
  total: number;
  /** Human text for the record levels that are on; null for use cases with none. */
  levelsText: string | null;
  recheckCount: number;
}

export interface ReviewVm {
  rows: ReviewRowVm[];
  assumedTotal: number;
  recheckTotal: number;
  /** Selected use cases whose own page still has Assumed inputs. */
  rowsWithAssumed: number;
}

function levelsTextOf(uc: SizedUseCase, sel: UseCaseSelection): string | null {
  if (uc.methods.length > 0) {
    const on = enabledMethods(uc, sel.methods).map((m) => methodLetter(uc, m));
    return `${on.length > 1 ? 'Methods' : 'Method'} ${on.join(' + ')}`;
  }
  if (uc.recordLevels.length === 0) return null;
  return enabledLevels(uc, sel.recordLevels)
    .map((l) => LEVEL_LABEL[l])
    .join(' + ');
}

export function buildReviewVm(sizing: CalculatorInput, flags: readonly Recheck[]): ReviewVm {
  const rows: ReviewRowVm[] = [];
  for (const sel of orderSelection([...sizing.selected])) {
    const uc = getSizedUseCase(sel.id);
    if (!uc) continue;
    const { assumed, total } = countInputsOnDefaults(
      sel.id,
      sel.answers,
      sel.recordLevels,
      { tools: sizing.tools, customTools: sizing.customTools },
      sel.methods,
    );
    rows.push({
      id: uc.id,
      name: uc.name,
      tier: uc.tier,
      assumed,
      total,
      levelsText: levelsTextOf(uc, sel),
      recheckCount: flags.filter((f) => f.useCaseId === uc.id).length,
    });
  }
  return {
    rows,
    assumedTotal: rows.reduce((t, r) => t + r.assumed, 0),
    recheckTotal: flags.length,
    rowsWithAssumed: rows.filter((r) => r.assumed > 0).length,
  };
}

/** "3 of 8 inputs assumed" / "All inputs confirmed". The tool checklist counts as one input. */
export function assumedText(row: Pick<ReviewRowVm, 'assumed' | 'total'>): string {
  if (row.total === 0 || row.assumed === 0) return 'All inputs confirmed';
  if (row.assumed === row.total) return `All ${row.total} ${row.total === 1 ? 'input' : 'inputs'} assumed`;
  return `${row.assumed} of ${row.total} inputs assumed`;
}

/* --------------------------------------------------------------------------- history */

export interface HistoryEntryVm {
  /** The snapshot's ISO date (unique per key: one snapshot a day). */
  date: string;
  dateLabel: string;
  valueText: string;
  /** This entry reverted the value to the Clay default. */
  reverted: boolean;
}

export interface HistoryItemVm {
  /** The input question key or the leading-metric (`Metric_Definition__c`) key. */
  key: string;
  label: string;
  kind: 'metric' | 'input';
  entries: HistoryEntryVm[];
}

function dateLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function entryVm(e: MetricHistoryEntry, format: (v: number) => string): HistoryEntryVm {
  return { date: e.date, dateLabel: dateLabel(e.date), valueText: e.value === '' ? '—' : format(e.value), reverted: e.assumed };
}

/**
 * The change log for one use case: its leading metrics first (keyed by the `uc-*` metric keys the
 * observations are written against), then its inputs. Items with no recorded change are omitted.
 */
export function historyItemsFor(history: MetricHistoryMap | null, useCaseId: string): HistoryItemVm[] {
  const uc = getSizedUseCase(useCaseId);
  const mine = history?.[useCaseId];
  if (!uc || !mine) return [];
  const items: HistoryItemVm[] = [];
  for (const m of uc.leadingMetrics) {
    const entries = mine[m.key];
    if (entries?.length) {
      items.push({ key: m.key, label: m.name, kind: 'metric', entries: entries.map((e) => entryVm(e, (v) => formatMetric(v, m.unit))) });
    }
  }
  for (const q of uc.questions) {
    const entries = mine[q.key];
    if (entries?.length) {
      items.push({ key: q.key, label: q.label, kind: 'input', entries: entries.map((e) => entryVm(e, (v) => fmtInput(q.kind, v))) });
    }
  }
  return items;
}

/* ------------------------------------------------------------------- the review table */

/** `needed` = a required deal input with no value yet (current annual pipeline). */
export type ReviewStatus = 'assumed' | 'collected' | 'recheck' | 'needed';

export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  assumed: 'Assumed',
  collected: 'Collected',
  recheck: 'Re-check',
  needed: 'Needed',
};

export type ReviewUnit = '$' | '%' | 'hrs' | 'min' | '';

export type ReviewRowSource =
  | { type: 'deal'; field: 'acv' | 'winRate' | 'currentAnnualPipeline' }
  | { type: 'answer'; key: string; kind: string }
  | { type: 'factor'; which: 'realization' | 'attribution' }
  /** One selected displacement tool: a built-in vendor (`toolId`) or a custom tool (`index` into customTools). */
  | { type: 'tool'; toolId: string | null; index: number | null; defaultSpend: number | null };

export interface ReviewTableRow {
  id: string;
  /** Includes the record-level suffix when both levels are on. */
  label: string;
  unit: ReviewUnit;
  source: ReviewRowSource;
  status: ReviewStatus;
  /** Why migration flagged it (the tooltip on a Re-check status); null otherwise. */
  reason: string | null;
}

export interface ReviewTableGroup {
  /** `deal`, or the use case id. */
  id: string;
  kind: 'deal' | 'use-case';
  name: string;
  tier: SizedUseCase['tier'] | null;
  /** Program value (use case groups only), shown beside the Clay-attributed figure. */
  programText: string | null;
  /** Clay-attributed value (use case groups only). */
  attributedText: string | null;
  /** "Clay-attributed revenue" or "Clay-attributed cost savings", by the use case's outcome. */
  attributedLabel: string | null;
  /** Overlap notes, shown under the group header. */
  notes: string[];
  /** Flags that do not belong to one row (dismissable under the header). */
  looseFlags: RecheckVm[];
  rows: ReviewTableRow[];
}

const UNIT_OF: Record<string, ReviewUnit> = { money: '$', rate: '$', pct: '%', hrs: 'hrs', min: 'min' };
const DEAL_FIELD_LABEL = {
  acv: ['Average deal size (ACV)', '$'],
  winRate: ['Win rate', '%'],
  currentAnnualPipeline: ['Current annual pipeline', '$'],
} as const;

/** Flags whose key matches a row mark that row; the rest stay loose. */
function claim(flags: readonly Recheck[], keysFor: (key: string) => string[]) {
  const reasons = new Map<string, string>();
  const loose: Recheck[] = [];
  for (const f of flags) {
    const rowIds = keysFor(f.key);
    if (rowIds.length === 0) loose.push(f);
    for (const id of rowIds) if (!reasons.has(id)) reasons.set(id, f.reason);
  }
  return { reasons, loose: loose.map(recheckVm) };
}

function statusOf(assumed: boolean, reason: string | undefined): ReviewStatus {
  return reason !== undefined ? 'recheck' : assumed ? 'assumed' : 'collected';
}

/**
 * One compact table grouped by use case (story 17): Deal economics first, then per use case its
 * input questions, realization and attribution. Defaults are never rendered here; a row is just
 * Assumed (still on the Clay default), Collected (the customer gave it) or Re-check (a migration
 * flag, which clears when the value is edited).
 */
export function buildReviewTable(sizing: CalculatorInput, flags: readonly Recheck[]): ReviewTableGroup[] {
  const ids = sizing.selected.map((s) => s.id);
  const { rollup } = computeCalculator(sizing);
  const groups: ReviewTableGroup[] = [];

  // Deal economics
  const fields: Array<'acv' | 'winRate' | 'currentAnnualPipeline'> = ['acv', 'winRate'];
  if (requiresCurrentAnnualPipeline(ids)) fields.push('currentAnnualPipeline');
  const dealClaim = claim(dealRecheck(flags).map((r) => r.flag), (k) => (fields.some((f) => f === k) ? [k] : []));
  groups.push({
    id: 'deal',
    kind: 'deal',
    name: 'Deal economics',
    tier: null,
    programText: null,
    attributedText: null,
    attributedLabel: null,
    notes: [],
    looseFlags: dealClaim.loose,
    rows: fields.map((field) => {
      const reason = dealClaim.reasons.get(field);
      const [label, unit] = DEAL_FIELD_LABEL[field];
      let status: ReviewStatus;
      if (field === 'currentAnnualPipeline') {
        // Like ACV and win rate it carries a default; only a blank or zero value is Needed.
        status =
          reason !== undefined
            ? 'recheck'
            : !(Number(sizing.deal.currentAnnualPipeline) > 0)
              ? 'needed'
              : statusOf(sizing.deal.currentAnnualPipeline === DEFAULT_CURRENT_ANNUAL_PIPELINE, undefined);
      } else {
        const onDefault = field === 'acv' ? sizing.deal.acv === DEFAULT_ACV : sizing.deal.winRate === DEFAULT_WIN_RATE;
        status = statusOf(onDefault, reason);
      }
      return { id: `deal:${field}`, label, unit, source: { type: 'deal', field }, status, reason: reason ?? null };
    }),
  });

  for (const sel of orderSelection([...sizing.selected])) {
    const uc = getSizedUseCase(sel.id);
    if (!uc) continue;
    const mine = flags.filter((f) => f.useCaseId === uc.id);
    const levels = enabledLevels(uc, sel.recordLevels);
    const methods = enabledMethods(uc, sel.methods);
    const isTools = uc.id === 'uc-tool-consolidation';
    const questions = isTools ? [] : questionGroups(uc, levels, sel.answers, sizing.deal.acv, methods).flatMap((g) => g.questions);
    const rowKeys = new Set(questions.map((q) => q.key));
    const c = claim(mine, (k) => {
      if (k === 'factors') return ['realization', 'attribution'];
      if (k === 'realization' || k === 'attribution') return [k];
      if (k === 'tools' && isTools) return ['tools'];
      return rowKeys.has(k) ? [k] : [];
    });
    const line = rollup.lines.find((l) => l.id === uc.id);
    const rows: ReviewTableRow[] = questions.map((q) => {
      const reason = c.reasons.get(q.key);
      return {
        id: `${uc.id}:${q.key}`,
        label: q.level ? `${q.label} · ${LEVEL_LABEL[q.level]}` : q.method ? `${q.label} · Method ${methodLetter(uc, q.method)}` : q.label,
        unit: UNIT_OF[q.kind] ?? '',
        source: { type: 'answer', key: q.key, kind: q.kind },
        status: statusOf(q.assumed, reason),
        reason: reason ?? null,
      };
    });
    if (isTools) {
      // One row per selected tool, same shape as every other group: built-in vendors ticked on the
      // use case page, then custom tools. Assumed while the contract value is still the vendor's default.
      const reason = c.reasons.get('tools');
      const effective = sizing.tools ?? DEFAULT_TOOLS16;
      for (const t of TOOLS16) {
        if (effective[t.id] == null) continue;
        rows.push({
          id: `${uc.id}:tool:${t.id}`,
          label: t.name,
          unit: '$',
          source: { type: 'tool', toolId: t.id, index: null, defaultSpend: t.def },
          status: statusOf(Number(effective[t.id]) === t.def, reason),
          reason: reason ?? null,
        });
      }
      (sizing.customTools ?? []).forEach((ct, index) => {
        if (!(ct.name || '').trim() && !(Number(ct.spend) > 0)) return;
        rows.push({
          id: `${uc.id}:custom:${index}`,
          label: (ct.name || '').trim() || 'Custom tool',
          unit: '$',
          source: { type: 'tool', toolId: null, index, defaultSpend: null },
          status: statusOf(false, reason),
          reason: reason ?? null,
        });
      });
    }
    for (const which of ['realization', 'attribution'] as const) {
      const reason = c.reasons.get(which);
      const stored = sel[which];
      rows.push({
        id: `${uc.id}:${which}`,
        label: which === 'realization' ? 'Realization: how much of this value lands' : 'Attribution: Clay’s share of the credit',
        unit: '%',
        source: { type: 'factor', which },
        status: statusOf(stored == null || stored === '', reason),
        reason: reason ?? null,
      });
    }
    groups.push({
      id: uc.id,
      kind: 'use-case',
      name: uc.name,
      tier: uc.tier,
      programText: moneyBig(line?.programValue ?? 0),
      attributedText: moneyBig(line?.attributedValue ?? 0),
      attributedLabel: attributedLabelFor(uc.outcome),
      notes: overlapNotesFor(uc.id, ids).map((o) => {
        const other = o.between.find((x) => x !== uc.id) ?? '';
        return `Overlaps with ${getSizedUseCase(other)?.name ?? other}. ${o.note}`;
      }),
      looseFlags: c.loose,
      rows,
    });
  }
  return groups;
}
